import {
  EnumLike,
  ZodDiscriminatedUnion,
  ZodIntersection,
  ZodObject,
  ZodType,
  ZodUnion,
  ZodUnionOptions,
  ZodEffects,
  z,
} from 'zod';
import { DateTime } from 'luxon';
import { normalizeE164, normalizeTPhone, readStoredTPhone } from '~/lib/phone';

export type ZodCoerceBoolean = z.ZodEffects<
  z.ZodUnion<[z.ZodBoolean, z.ZodLiteral<'true'>, z.ZodLiteral<'false'>, z.ZodLiteral<''>, z.ZodUndefined]>,
  boolean
>;

/**
 * @see https://github.com/colinhacks/zod/issues/1630
 */
export function coerceBoolean(): ZodCoerceBoolean {
  return z
    .union([z.boolean(), z.literal('true'), z.literal('false'), z.literal(''), z.undefined()])
    .transform((value) => value === true || value === 'true');
}

export function coerceTrue() {
  return z.union([z.boolean(), z.literal('true')]).transform((value) => value === true || value === 'true');
}

export function coerceFalse() {
  return z
    .union([z.literal('false'), z.literal(''), z.undefined()])
    .transform((value) => value === 'false' || value === '' || value === undefined);
}

export function zodJson<O extends ZodObject<any>>(schema: O) {
  return z.preprocess((v) => (typeof v === 'string' ? JSON.parse(v) : v), schema);
}

export function clean_phone(phone: string): TPhone {
  const normalized = normalizeTPhone(phone);

  if (!normalized) {
    throw new Error(`Invalid phone: ${phone.trim()}`);
  }

  return normalized;
}

const TPHONE_MESSAGE = 'Must be a valid phone number (starting with +1, eg: +19876543210).';

/**
 * A US phone number (TPhone, `+1` and ten digits) from what someone typed or
 * what is stored: formatting removed, a bare 10-digit number given its `+1`,
 * a typed leading `1` read as the trunk prefix (lib/phone.ts). Anything else,
 * a non-US number included, is rejected rather than turned into a `+1` one.
 *
 * Until Oct 2026 this put `+1` before whatever digits it found and checked
 * the result with an UNANCHORED pattern, so `+44 7911 123456` passed as
 * `+1447911123456` and `303-555-0142 x12` as `+1303555014212`. Values it
 * stored from a typed leading `1` (`+113035550142`) are read back as the
 * right number (readStoredTPhone); the other garbage no longer parses.
 */
function zodTPhoneString() {
  return z.coerce.string().transform((value, ctx): TPhone => {
    const phone = readStoredTPhone(value);

    if (!phone) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: TPHONE_MESSAGE });

      return z.NEVER;
    }

    return phone;
  });
}

export function zodTPhoneOptional() {
  return zodTPhoneString().or(z.union([z.literal(''), z.undefined(), z.null()]).transform(() => undefined));
}

export function zodTPhone() {
  return zodTPhoneString();
}

/**
 * Any E.164 number (lib/phone.ts normalizeE164): what Twilio sends as an
 * inbound text's `From`, which may be outside the US. Never forced to `+1`.
 */
export function zodE164() {
  return z.coerce.string().transform((value, ctx): string => {
    const phone = normalizeE164(value);

    if (!phone) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Must be a phone number in international format (eg: +19876543210).',
      });

      return z.NEVER;
    }

    return phone;
  });
}

/**
 * THE PRE-OCT-2026 PARSE, for the IVR and TaskRouter routes only: `+1` in
 * front of whatever digits there are, checked without anchors. An
 * international caller is read as a wrong `+1…` number but still reaches the
 * queue, as before; the strict zodTPhone would answer them with the error
 * message and hang up. Kept so the SMS identity work does not change how
 * calls are handled; the IVR's own change should move these to zodE164.
 */
export function zodTPhoneLegacy() {
  return z.coerce
    .string()
    .transform(
      (v) =>
        `+1${v
          .trim()
          .replace(/^\+1/, '')
          .replace(/[^0-9]/g, '')}`,
    )
    .refine((v) => v.match(/\+1\d{10}/) !== null, TPHONE_MESSAGE)
    .transform((v) => v as TPhone);
}

export function zodTEmailRequired() {
  return z
    .preprocess(
      (v) => (typeof v === 'string' ? v.trim().toLowerCase() : v),
      z.string({ required_error: 'Email is required.' }).email({ message: 'Email is invalid.' }),
    )
    .transform((v) => v as TEmail);
}

export function zodUrlOptional(name = 'Url') {
  return z
    .string({ required_error: `${name} is required.` })
    .url({ message: `${name} is not a valid URL.` })
    .or(z.union([z.literal(''), z.undefined(), z.null()]).transform(() => undefined));
}

export function zodTEmailOptional() {
  return z
    .preprocess(
      (v) => (typeof v === 'string' ? v.trim().toLowerCase() : v),
      z.string({ required_error: 'Email is required.' }).email({ message: 'Email is invalid.' }),
    )
    .transform((v) => v as TEmail)
    .or(z.union([z.literal(''), z.undefined(), z.null()]).transform(() => undefined));
}

export function zodNativeEnum<T extends EnumLike>(values: T) {
  return z.coerce.string().min(1).pipe(z.nativeEnum(values));
}

export function zodNativeEnumOptional<T extends EnumLike>(values: T, optional = false) {
  const piped = z.coerce.string().min(1).pipe(z.nativeEnum(values));

  if (optional) {
    return piped.or(z.union([z.literal(''), z.undefined(), z.null()]).transform(() => undefined));
  }

  return piped;
}

export function zodTMoney() {
  return z.coerce
    .string()
    .refine(
      (v) => v.match(/^-?\d+(\.\d{1,2})?$/) !== null,
      'Must be a valid dollar amount (eg: 19.99) without a currency sign.',
    )
    .transform((v) => v as TMoney);
}

export function zodTMoneyOptional() {
  return z.coerce
    .string()
    .refine(
      (v) => v.match(/^-?\d+(\.\d{1,2})?$/) !== null,
      'Must be a valid dollar amount (eg: 19.99) without a currency sign.',
    )
    .transform((v) => v as TMoney)
    .or(z.union([z.literal(''), z.undefined(), z.null()]).transform(() => undefined));
}

export function zodTDateIso() {
  return z
    .string()
    .refine((v) => {
      try {
        DateTime.fromISO(v);
        return true;
      } catch (e) {
        return false;
      }
    }, 'Must be a valid ISO date (YYYY-MM-DDTHH:mm:ss.SSSZ)')
    .transform((v) => v as TDateISO);
}

export function zodTDateISODate(message?: string) {
  return z
    .string({ required_error: message })
    .refine((v) => {
      try {
        DateTime.fromSQL(v);
        return true;
      } catch (e) {
        return false;
      }
    }, message ?? 'Must be a valid day format (YYYY-MM-DD)')
    .transform((v) => v as TDateISODate);
}

export function zodTTime24(error = 'Time is required.') {
  return z
    .string({ required_error: error })
    // Luxon returns an invalid DateTime rather than throwing, so check isValid.
    .refine((v) => DateTime.fromFormat(v, 'HH:mm').isValid, 'Must be a valid 24-hour time (HH:mm)')
    .transform((v) => v as TTime24);
}

export function coerceIsoDate() {
  return z
    .string()
    .datetime({ offset: true })
    .transform((value) => new Date(value).toISOString());
}

/**
 * @see https://github.com/colinhacks/zod/issues/2461#issuecomment-1691426894
 */
export function coerceOptionalPositiveNumber(message? :string) {
  return z
    .literal('')
    .transform(() => undefined)
    .or(z.coerce.number().min(1, { message }).optional());
}

export const FIRST_VERSION_TIMESTAMP = 1696464000000; // Date.UTC(2023, 9, 5);

export function schema_latest_version<S extends ZodVersionedSchema>(
  schemas: S,
  metadata?: ZodVersionedMetadata<S>,
): keyof typeof schemas & number {
  const now = Date.now();

  if (metadata?.version) {
    return metadata.version;
  }

  let target = FIRST_VERSION_TIMESTAMP;

  for (const key of Object.keys(schemas)) {
    const version = parseInt(key);

    if (version >= now) {
      target = version;
    }
  }

  return target as keyof typeof schemas & number;
}

export function coerceLiteralNumberRequired<T extends number>(value: T) {
  return z.preprocess((v) => (typeof v === 'string' ? parseInt(v) : v), z.literal(value)) as any as z.ZodLiteral<T>;
}

export function coerceLiteralNumberOptional<T extends number>(value: T) {
  return coerceLiteralNumberRequired(value).or(z.union([z.literal(''), z.undefined()]).transform(() => undefined));
}

type VersionedObject = ZodObject<
  { version: z.ZodLiteral<typeof FIRST_VERSION_TIMESTAMP> },
  'strip',
  z.ZodTypeAny,
  { version: typeof FIRST_VERSION_TIMESTAMP }
>;

type TypeObject = ZodObject<{ type: z.ZodLiteral<string> }, 'strip', z.ZodTypeAny, { type: string }>;

export type ZodVersionedSchema = Record<
  typeof FIRST_VERSION_TIMESTAMP,
  | VersionedObject
  | ZodIntersection<VersionedObject, ZodDiscriminatedUnion<'type', TypeObject[]>>
  | ZodIntersection<VersionedObject, ZodUnion<ZodUnionOptions>>
  | ZodEffects<VersionedObject>
>;

export type ZodVersionedMetadata<Schema extends ZodVersionedSchema = ZodVersionedSchema> = {
  [Version in keyof Schema]: Schema[Version] extends ZodType ? z.TypeOf<Schema[Version]> : never;
}[keyof Schema];
